import React, { useState, useMemo, useEffect } from "react";
import {
  useGetAiUsageLogsQuery,
  useGetAiUsageLogStatsQuery,
} from "apis/aiUsageLog";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import { formatDateTime, formatCurrency } from "utils/helperFunctions";

const formatTokens = (value) => {
  if (value == null) return "-";
  return Number(value).toLocaleString();
};

const formatFeature = (feature) =>
  feature
    ? feature
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (char) => char.toUpperCase())
    : "-";

const PROVIDER_OPTIONS = [
  { value: "", label: "All Providers" },
  { value: "gemini", label: "Gemini" },
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
];

const FEATURE_OPTIONS = [
  { value: "", label: "All Features" },
  { value: "photoClassification", label: "Photo Classification" },
  { value: "textGeneration", label: "Text Generation" },
  { value: "chat", label: "Chat" },
  { value: "pilot", label: "Pilot" },
  { value: "photoEditValidation", label: "Photo Edit Validation" },
  { value: "photoEditImageGen", label: "Photo Edit Image Gen" },
  { value: "floorplanValidation", label: "Floorplan Validation" },
  { value: "floorplanImageGen", label: "Floorplan Image Gen" },
];

const TIME_FRAMES = [
  { value: "", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "lastWeek", label: "Last 7 Days" },
  { value: "lastMonth", label: "Last 30 Days" },
  { value: "last3Months", label: "Last 3 Months" },
  { value: "last6Months", label: "Last 6 Months" },
  { value: "custom", label: "Custom Range" },
];

const initialFilters = {
  provider: "gemini",
  feature: "",
  model: "",
  search: "",
  timeFrame: "",
  startDate: "",
  endDate: "",
};

const PAGE_SIZE = 25;

const getOption = (options, value) =>
  options.find((o) => o.value === value) || null;

const AiUsageLogs = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  const queryParams = useMemo(() => {
    const params = { page: currentPage, limit: PAGE_SIZE };
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        if (key === "timeFrame" && value === "custom") return;
        params[key] = value;
      }
    });
    return params;
  }, [appliedFilters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  const { data: logs, pagination, isPending, isFetching } =
    useGetAiUsageLogsQuery(queryParams);
  const { data: stats } = useGetAiUsageLogStatsQuery(queryParams);

  const isCustomRange = filters.timeFrame === "custom";

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">
            {formatDateTime(row.createdAt) ?? "-"}
          </span>
        ),
        isFilter: false,
        minWidth: "180px",
      },
      {
        accessorKey: "provider",
        header: "Provider",
        cell: (row) => (
          <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-800 capitalize">
            {row.provider || "-"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "feature",
        header: "Feature",
        cell: (row) => (
          <span className="px-2 py-0.5 rounded-full text-xs bg-lightergray text-darkergray">
            {formatFeature(row.feature)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "model",
        header: "Model",
        cell: (row) => (
          <span className="text-xs">{row.model || "-"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "usage.totalTokens",
        header: "Total Tokens",
        cell: (row) => formatTokens(row.usage?.totalTokens),
        isFilter: false,
      },
      {
        accessorKey: "cost.amount",
        header: "Est. Cost (USD)",
        cell: (row) => (
          <span className="text-xs font-medium">
            {formatCurrency(row.cost?.amount)}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "propertyAddress",
        header: "Property",
        cell: (row) => (
          <span>{row.propertyAddress || "-"}</span>
        ),
        isFilter: false,
        maxWidth: "260px",
      },
      {
        accessorKey: "durationMs",
        header: "Duration",
        cell: (row) => (row.durationMs ? `${row.durationMs} ms` : "-"),
        isFilter: false,
      },
    ],
    [],
  );

  if (isPending) return <PageLoader />;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold text-darkergray mb-2">
        AI Usage &amp; Cost
      </h1>
      <p className="text-sm text-darkgray mb-6">
        Estimated costs are shown in US dollars (USD) using provider-published
        token rates.
      </p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="API Calls" value={stats.totals?.total || 0} />
          <StatCard
            label="Total Tokens"
            value={formatTokens(stats.totals?.totalTokens || 0)}
          />
          <StatCard
            label="Est. Total Cost (USD)"
            value={formatCurrency(stats.totals?.totalCost || 0)}
            color="text-green-700"
          />
          <StatCard
            label="Input Tokens"
            value={formatTokens(stats.totals?.totalInputTokens || 0)}
          />
          <StatCard
            label="Output Tokens"
            value={formatTokens(stats.totals?.totalOutputTokens || 0)}
          />
          <StatCard
            label="Reasoning Tokens"
            value={formatTokens(stats.totals?.totalReasoningTokens || 0)}
          />
        </div>
      )}

      <div className="bg-white border border-mediumgray rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-darkgray mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <FilterField label="Provider">
            <SearchSelect
              options={PROVIDER_OPTIONS}
              value={getOption(PROVIDER_OPTIONS, filters.provider)}
              onChange={(opt) =>
                handleFilterChange("provider", opt?.value ?? "")
              }
              placeholder="All Providers"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Feature">
            <SearchSelect
              options={FEATURE_OPTIONS}
              value={getOption(FEATURE_OPTIONS, filters.feature)}
              onChange={(opt) =>
                handleFilterChange("feature", opt?.value || "")
              }
              placeholder="All Features"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Search">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="Property address, model…"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </FilterField>

          <FilterField label="Model">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="e.g. gemini-2.5-flash"
              value={filters.model}
              onChange={(e) => handleFilterChange("model", e.target.value)}
            />
          </FilterField>

          <FilterField label="Date Range">
            <SearchSelect
              options={TIME_FRAMES}
              value={getOption(TIME_FRAMES, filters.timeFrame)}
              onChange={(opt) =>
                handleFilterChange("timeFrame", opt?.value || "")
              }
              placeholder="All Time"
              searchable={false}
            />
          </FilterField>

          {isCustomRange && (
            <>
              <FilterField label="Start Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </FilterField>
              <FilterField label="End Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </FilterField>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button className="gray-button" onClick={clearFilters}>
            Clear
          </Button>
          <Button className="black-button" onClick={applyFilters}>
            Apply Filters
          </Button>
        </div>
      </div>

      <Table
        title={isFetching ? "Usage Logs (loading…)" : "Usage Logs"}
        columns={columns}
        data={logs || []}
        pageSize={PAGE_SIZE}
        searchable={false}
        serverPagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                limit: pagination.limit,
              }
            : null
        }
        onPageChange={setCurrentPage}
        onRowClick={(row) => setSelectedLog(row)}
        emptyText="No logs found."
      />

      <LogDetailModal
        log={selectedLog}
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
      />
    </div>
  );
};

const FilterField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-darkergray mb-1">
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({ label, value, color = "text-darkergray" }) => (
  <div className="bg-white border border-mediumgray rounded-lg p-3 text-center">
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-darkgray uppercase tracking-wide">{label}</div>
  </div>
);

const LogDetailModal = ({ log, isOpen, onClose }) => {
  if (!log) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Usage Log Detail"
      className="max-w-3xl"
    >
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DetailRow label="Created" value={formatDateTime(log.createdAt) ?? "-"} />
          <DetailRow label="Provider" value={log.provider || "-"} />
          <DetailRow label="Feature" value={formatFeature(log.feature)} />
          <DetailRow label="Operation" value={formatFeature(log.operation)} />
          <DetailRow label="Model" value={log.model || "-"} />
          <DetailRow label="Status" value={log.status || "-"} />
          <DetailRow
            label="Estimated Cost (USD)"
            value={formatCurrency(log.cost?.amount)}
          />
          <DetailRow
            label="Input Tokens"
            value={formatTokens(log.usage?.inputTokens)}
          />
          <DetailRow
            label="Output Tokens"
            value={formatTokens(log.usage?.outputTokens)}
          />
          <DetailRow
            label="Reasoning Tokens"
            value={formatTokens(log.usage?.reasoningTokens)}
          />
          <DetailRow
            label="Cached Tokens"
            value={formatTokens(log.usage?.cachedTokens)}
          />
          <DetailRow
            label="Total Tokens"
            value={formatTokens(log.usage?.totalTokens)}
          />
          <DetailRow
            label="Duration"
            value={log.durationMs ? `${log.durationMs} ms` : "-"}
          />
          <DetailRow label="Property" value={log.propertyAddress || "-"} />
          <DetailRow label="Image ID" value={log.imageId ?? "-"} />
          <DetailRow label="Event ID" value={log.eventId || "-"} />
          <DetailRow label="Revision ID" value={log.revisionId || "-"} />
          <DetailRow label="User ID" value={log.userId || "-"} />
          {log.errorMessage && (
            <DetailRow label="Error" value={log.errorMessage} />
          )}
        </div>
      </div>
    </Modal>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wide text-darkgray">
      {label}
    </span>
    <span className="text-darkergray break-words">{String(value ?? "-")}</span>
  </div>
);

export default AiUsageLogs;
